/* eslint @typescript-eslint/no-unused-vars:0 */
import {
  Form,
  Loading,
  Title,
  useGetOne,
  useLogout,
  useNotify,
  usePermissions,
  useTheme,
  useTranslate
} from 'react-admin'
import {
  Card,
  CardContent,
  Grid,
  Button,
  Typography,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import log from 'loglevel'
import React from 'react'
import { type Partner, type Product } from '@mzc-watchover/adminapp-appsync'
import {
  find,
  includes,
  intersection,
  isNil,
  last,
  replace,
  reverse,
  size,
  split
} from 'lodash/fp'

import { Announce } from 'components/Announce'
import { Accounts } from 'components/Accounts'
import { Mapping } from 'components/Mapping'
import { Spot } from 'components/Spot'
import { Compare } from 'components/Compare'
import { DateInput, ScrollTo } from 'components/Util'
import { SpotData2 } from 'components/SpotData2'
import { AllData } from 'components/AllData'
import { useGetConfig } from '../DataProvider'
import { getStep } from 'components/Step'
import { useTour } from '@reactour/tour'
import { Footer } from 'components/Footer/inex'
import { getCognitoLog, getWeeklyLog } from 'components/DataProvider/functions'
import {
  addDays,
  eachWeekendOfMonth,
  endOfYear,
  format,
  getDay,
  isAfter,
  isBefore,
  parseISO,
  subDays
} from 'date-fns/fp'
import { useFormContext } from 'react-hook-form'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

export type ConfigProps = Record<string, any>

const DashboardContent: React.FC<{
  partnerName: string
  partner: Partial<Partner>
  config: ConfigProps & { Chart: ConfigProps }
}> = ({ partnerName, partner, config }) => {
  const notify = useNotify()
  const [open, setOpen] = React.useState(false)
  const [kind, setKind] = React.useState<string | undefined>(undefined)
  const { getValues } = useFormContext()
  const steps = getStep('Dashboard')
  const { setIsOpen, setSteps, setCurrentStep } = useTour()
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const isLarge = useMediaQuery((theme: any) => theme.breakpoints.up('lg'))
  const translate = useTranslate()
  const buttonWidth = isDesktop ? (isLarge ? '230px' : '210px') : '450px'
  const buttonHeight = isDesktop ? '60px' : '40px'
  const buttonSx = isDesktop
    ? {
        marginTop: '1em',
        marginLeft: '2em',
        marginRight: '2em',
        marginBottom: '1em'
      }
    : {
        marginTop: '0.5em',
        marginLeft: '2em',
        marginRight: '2em',
        marginBottom: '0.5em'
      }
  const [accounts, setAccounts] = React.useState(false)
  const [mappings, setMappings] = React.useState(false)
  const [spot, setSpot] = React.useState(false)
  const [spotData, setSpotData] = React.useState(false)
  const [compare, setCompare] = React.useState(false)
  const [allData, setAllData] = React.useState(false)
  const { permissions, isLoading } = usePermissions()
  const [scroll, setScroll] = React.useState<
    React.RefObject<HTMLDivElement> | undefined
  >(undefined)
  const handleAccounts = (): void => {
    setAccounts(true)
    setMappings(false)
    setSpot(false)
    setSpotData(false)
    setCompare(false)
    setAllData(false)
    if (!isNil(scroll) && !isNil(scroll.current)) {
      scroll.current.scrollIntoView()
    }
  }
  const handleMappings = (): void => {
    setAccounts(false)
    setMappings(true)
    setSpot(false)
    setSpotData(false)
    setCompare(false)
    setAllData(false)
    if (!isNil(scroll) && !isNil(scroll.current)) {
      scroll.current.scrollIntoView()
    }
  }
  const handleSpot = (): void => {
    setAccounts(false)
    setMappings(false)
    setSpot(true)
    setSpotData(false)
    setCompare(false)
    setAllData(false)
    if (!isNil(scroll) && !isNil(scroll.current)) {
      scroll.current.scrollIntoView()
    }
  }
  const handleSpotData = (): void => {
    setAccounts(false)
    setMappings(false)
    setSpot(false)
    setSpotData(true)
    setCompare(false)
    setAllData(false)
    if (!isNil(scroll) && !isNil(scroll.current)) {
      scroll.current.scrollIntoView()
    }
  }
  const handleCompare = (): void => {
    setAccounts(false)
    setMappings(false)
    setSpot(false)
    setSpotData(false)
    setCompare(true)
    setAllData(false)
    if (!isNil(scroll) && !isNil(scroll.current)) {
      scroll.current.scrollIntoView()
    }
  }
  const handleSummary = (): void => {
    setAccounts(false)
    setMappings(false)
    setSpot(false)
    setSpotData(false)
    setCompare(false)
    setAllData(true)
    if (!isNil(scroll) && !isNil(scroll.current)) {
      scroll.current.scrollIntoView()
    }
  }
  const handleCognitoLog = (dateArg?: string): void => {
    notify('downloading', { autoHideDuration: null })
    const enhanced = !isNil(dateArg)
    const date = enhanced ? dateArg : format('yyyy-MM-dd')(Date.now())
    getCognitoLog(partner.id as string, date, enhanced)
      .then(({ data }) => {
        if (!isNil(data)) {
          const binaryString = window.atob(data)
          const len = binaryString.length
          const bytes = new Uint8Array(len)
          for (let i = 0; i < len; ++i) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          const blob = new Blob([bytes], {
            type: !enhanced
              ? 'application/zip'
              : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const url = URL.createObjectURL(blob)
          const download = document.createElement('a')
          download.href = url
          download.download = `${format('yyyy年M月')(
            parseISO(date)
          )}ログインログ.${!enhanced ? 'zip' : 'xlsx'}`
          download.click()
          window.URL.revokeObjectURL(url)
        }
      })
      .catch(log.error)
      .finally(() => {
        notify('downloading', { autoHideDuration: 0 })
      })
  }
  const handleWeeklyLog = (date: string): void => {
    notify('downloading', { autoHideDuration: null })
    getWeeklyLog(partner.id as string, date)
      .then(({ data }) => {
        if (!isNil(data)) {
          const binaryString = window.atob(data)
          const len = binaryString.length
          const bytes = new Uint8Array(len)
          for (let i = 0; i < len; ++i) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          const blob = new Blob([bytes], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const url = URL.createObjectURL(blob)
          const download = document.createElement('a')
          download.href = url
          download.download = `週次実績-${replace(/-/g, '')(date)}.xlsx`
          download.click()
          window.URL.revokeObjectURL(url)
        }
      })
      .catch(log.error)
      .finally(() => {
        notify('downloading', { autoHideDuration: 0 })
      })
  }
  React.useEffect(() => {
    if (isNil(steps) || isNil(setSteps)) return
    setSteps(steps)
    setCurrentStep(0)
    setIsOpen(true)
  }, [])
  const handleClose = (): void => {
    setOpen(false)
  }
  const dialog = React.useMemo(() => {
    const onClick = (): void => {
      let date: string
      const { date: dateValue } = getValues()
      if (kind === 'cognitoLog') {
        const date = find(
          (item: Date) => !isAfter(Date.now())(item) && getDay(item) === 0
        )(reverse(eachWeekendOfMonth(parseISO(dateValue))))
        if (!isNil(date)) {
          log.debug({ date })
          handleCognitoLog(format('yyyy-MM-dd')(date))
        }
      } else if (kind === 'weeklyLog') {
        if (
          isBefore(subDays(getDay(Date.now()))(Date.now()))(parseISO(dateValue))
        ) {
          date = format('yyyy-MM-dd')(
            addDays(7 - getDay(parseISO(dateValue)))(parseISO(dateValue))
          )
        } else {
          date = format('yyyy-MM-dd')(
            subDays(getDay(parseISO(dateValue)))(parseISO(dateValue))
          )
        }
        handleWeeklyLog(date)
      }
      setOpen(false)
    }
    return (
      <Dialog onClose={handleClose} open={open} maxWidth="md" fullWidth>
        <DialogTitle>
          {translate(
            `resources.dashboard.dialogs.select${
              kind === 'cognitoLog' ? 'Month' : 'Date'
            }.title`
          )}
        </DialogTitle>
        <DialogContent>
          <DateInput
            label=""
            source="date"
            disableFuture
            views={
              kind === 'cognitoLog'
                ? ['year', 'month']
                : ['year', 'month', 'day']
            }
            minDate={parseISO('2025-01-01')}
            maxDate={endOfYear(Date.now())}
            defaultValue={format('yyyy-MM-dd')(
              subDays(getDay(Date.now()))(Date.now())
            )}
          />
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: 'flex-start', ml: '1em', mt: '-1em' }}>
          <Button variant="contained" onClick={onClick}>
            {translate(`resources.dashboard.dialogs.selectDate.buttons.exec`)}
          </Button>
          <Button variant="outlined" onClick={handleClose}>
            {translate(`resources.dashboard.dialogs.selectDate.buttons.cancel`)}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }, [handleClose, open, permissions])
  if (isLoading) return <Loading />
  return (
    <>
      <Grid container justifyContent="center">
        <Title title={`${partnerName}`} />
        <>
          <Announce partner={partner.id as string} />
          <Card
            raised
            sx={{
              width: '1280px',
              marginTop: '1em',
              marginBottom: '1em',
              backgroundColor: 'background'
            }}>
            <CardContent>
              <Grid container justifyContent="center">
                {size(
                  intersection(['owner', 'manager', 'highManager'])(permissions)
                ) > 0 && (
                  <Grid item sx={buttonSx}>
                    <Button
                      id="spot"
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        width: buttonWidth,
                        height: buttonHeight,
                        borderRadius: '10px'
                      }}
                      onClick={handleSpot}>
                      <Typography variant="h6">
                        {translate('resources.dashboard.menu.spot')}
                      </Typography>
                    </Button>
                  </Grid>
                )}
                {size(
                  intersection(['owner', 'manager', 'highManager'])(permissions)
                ) > 0 && (
                  <Grid item sx={buttonSx}>
                    <Button
                      id="spotData"
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        width: buttonWidth,
                        height: buttonHeight,
                        borderRadius: '10px'
                      }}
                      onClick={handleSpotData}>
                      <Typography variant="h6">
                        {translate('resources.dashboard.menu.spotData')}
                      </Typography>
                    </Button>
                  </Grid>
                )}
                {size(
                  intersection(['owner', 'manager', 'highManager'])(permissions)
                ) > 0 && (
                  <Grid item sx={buttonSx}>
                    <Button
                      id="compare"
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        width: buttonWidth,
                        height: buttonHeight,
                        borderRadius: '10px'
                      }}
                      onClick={handleCompare}>
                      <Typography variant="h6">
                        {translate('resources.dashboard.menu.compare')}
                      </Typography>
                    </Button>
                  </Grid>
                )}
                {includes('highManager')(permissions) && (
                  <Grid item sx={buttonSx}>
                    <Button
                      id="summary"
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        width: buttonWidth,
                        height: buttonHeight,
                        borderRadius: '10px'
                      }}
                      onClick={handleSummary}>
                      <Typography variant="h6">
                        {translate('resources.dashboard.menu.summary')}
                      </Typography>
                    </Button>
                  </Grid>
                )}
                {includes('admin')(permissions) && (
                  <Grid item sx={buttonSx}>
                    <Button
                      id="mappings"
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        width: buttonWidth,
                        height: buttonHeight,
                        borderRadius: '10px'
                      }}
                      onClick={handleMappings}>
                      <Typography variant="h6">
                        {translate('resources.dashboard.menu.admin.mappings')}
                      </Typography>
                    </Button>
                  </Grid>
                )}
                {includes('admin')(permissions) && (
                  <Grid item sx={buttonSx}>
                    <Button
                      id="accounts"
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        width: buttonWidth,
                        height: buttonHeight,
                        borderRadius: '10px'
                      }}
                      onClick={handleAccounts}>
                      <Typography variant="h6">
                        {translate('resources.dashboard.menu.admin.accounts')}
                      </Typography>
                    </Button>
                  </Grid>
                )}
                {includes('highManager')(permissions) &&
                  translate(
                    'resources.dashboard.menu.highManager.cognitoLog'
                  ) !== 'resources.dashboard.menu.highManager.cognitoLog' && (
                    <Grid item sx={buttonSx}>
                      <Button
                        id="cognitoLog"
                        variant="contained"
                        color="primary"
                        size="large"
                        sx={{
                          width: buttonWidth,
                          height: buttonHeight,
                          borderRadius: '10px'
                        }}
                        onClick={() => {
                          setKind('cognitoLog')
                          setOpen(true)
                        }}>
                        <Typography variant="h6">
                          {translate(
                            'resources.dashboard.menu.highManager.cognitoLog'
                          )}
                        </Typography>
                      </Button>
                    </Grid>
                  )}
                {includes('mzc')(permissions) &&
                  translate(
                    'resources.dashboard.menu.highManager.weeklyData'
                  ) !== 'resources.dashboard.menu.highManager.weeklyData' && (
                    <Grid item sx={buttonSx}>
                      <Button
                        id="weeklyData"
                        variant="contained"
                        color="primary"
                        size="large"
                        sx={{
                          width: buttonWidth,
                          height: buttonHeight,
                          borderRadius: '10px'
                        }}
                        onClick={() => {
                          setKind('weeklyLog')
                          setOpen(true)
                        }}>
                        <Typography variant="h6">
                          {translate(
                            'resources.dashboard.menu.highManager.weeklyData'
                          )}
                        </Typography>
                      </Button>
                    </Grid>
                  )}
              </Grid>
            </CardContent>
          </Card>
        </>
        {/* {edit && <UserEdit partner={partner} />} */}
        {(accounts || mappings || spot || compare || spotData || allData) && (
          <Card
            sx={{
              width: '1280px',
              marginTop: '1em',
              marginBottom: '1em',
              backgroundColor: 'background'
            }}>
            <CardContent>
              <Grid item xs={12}>
                {accounts && (
                  <ScrollTo
                    setScroll={setScroll}
                    renderItem={() => (
                      <Accounts config={config} partner={partner} />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                {mappings && (
                  <ScrollTo
                    setScroll={setScroll}
                    renderItem={() => (
                      <Mapping config={config} partner={partner} />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                {spot && !isNil(partner.id) && (
                  <ScrollTo
                    setScroll={setScroll}
                    renderItem={(props) => (
                      <Spot
                        {...props}
                        partner={partner.id as string}
                        config={config}
                      />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                {compare && <Compare config={config} />}
              </Grid>
              <Grid item xs={12}>
                {spotData && (
                  <SpotData2 config={config} partner={partner.id as string} />
                )}
              </Grid>
              <Grid item xs={12}>
                {allData && (
                  <AllData config={config} partner={partner.id as string} />
                )}
              </Grid>
            </CardContent>
          </Card>
        )}
      </Grid>
      {dialog}
    </>
  )
}

export const Dashboard: React.FC<{
  partner: string
}> = ({ partner }) => {
  const [_products, setProducts] = React.useState<
    Array<Partial<Product>> | undefined
  >(undefined)
  const { data: configData, isLoading: isConfigLoading } = useGetConfig(partner)
  const [partnerName, setPartnerName] = React.useState<string | undefined>(
    undefined
  )
  const [_theme, setTheme] = useTheme()
  const notify = useNotify()
  const logout = useLogout()
  const { data, isLoading } = useGetOne<{ id: string } & Partial<Partner>>(
    'partners',
    { id: partner }
  )
  React.useEffect(() => {
    if (isLoading) return
    setTheme('light')
    if (!isNil(data)) {
      const { name, products } = data
      setPartnerName(`${name as string}さま`)
      setProducts(products)
    }
  }, [isLoading])
  if (isLoading || isConfigLoading) return <Loading />
  if (isNil(data) || isNil(configData)) {
    notify('resources.dashboard.notification.cannotUse', { type: 'error' })
    logout()
      .then(() => {})
      .catch(log.error)
    return <></>
  }
  return (
    <>
      <Form>
        <DashboardContent
          partnerName={partnerName ?? ''}
          partner={data}
          config={configData as any}
        />
      </Form>
      <Footer />
    </>
  )
}
export const hasConfig = (
  config: Record<string, any>,
  keys: string
): boolean => {
  if (isNil(config)) return false
  let item = config
  for (const key of split('.')(keys)) {
    if (isNil(item[key])) return false
    item = item[key]
  }
  return true
}
